import React from 'react';
import CourseTemplate from '../../components/CourseTemplate';

const MedicalRecordScienceCoursePage: React.FC = () => {
  const medicalRecordData = {
    title: "Bachelor of Science in Medical Record Science",
    shortName: "B.Sc MRS",
    duration: "3 Years",
    description: "A specialized program focusing on health information management, medical coding, healthcare data analysis, and electronic health records administration.",
    overview: "B.Sc Medical Record Science prepares students to become health information professionals who manage patient health information and medical records. The program covers medical coding, health information systems, healthcare law, data analysis, and privacy regulations. Students learn to organize, analyze, and technically evaluate health information data, ensuring accuracy, accessibility, and security of patient records in various healthcare settings.",
    eligibility: [
      "Completed 10+2 in any stream (Science preferred)",
      "Minimum 50% aggregate marks (45% for SC/ST/OBC)",
      "Age: 17-25 years",
      "Interest in healthcare administration",
      "Good analytical and computer skills",
      "Attention to detail and accuracy"
    ],
    careerProspects: {
      roles: [
        "Medical Records Administrator",
        "Health Information Manager",
        "Medical Coder",
        "Clinical Data Coordinator",
        "EHR Specialist",
        "Healthcare Data Analyst",
        "Quality Assurance Specialist",
        "Health Information Technician",
        "Privacy Officer",
        "Healthcare Compliance Officer"
      ],
      industries: [
        "Hospitals and Healthcare Systems",
        "Health Insurance Companies",
        "Government Health Agencies",
        "Electronic Health Record Companies",
        "Healthcare Consulting Firms",
        "Medical Coding Companies",
        "Research Institutions",
        "Pharmaceutical Companies",
        "Healthcare Technology Companies",
        "Telemedicine Platforms"
      ],
      averageSalary: "₹3-6 LPA (varies by experience and specialization)",
      topRecruiters: [
        "Apollo Hospitals",
        "Fortis Healthcare",
        "Max Healthcare",
        "Manipal Hospitals",
        "Health Insurance Companies",
        "Government Hospitals",
        "HCL Healthcare",
        "Cognizant Healthcare",
        "Wipro Healthcare",
        "Medical Coding Companies"
      ]
    },
    curriculum: [
      {
        year: 1,
        subjects: [
          "Medical Terminology",
          "Human Anatomy and Physiology",
          "Healthcare Delivery Systems",
          "Computer Applications",
          "Statistics",
          "Communication Skills",
          "Introduction to Health Information",
          "Medical Office Procedures"
        ]
      },
      {
        year: 2,
        subjects: [
          "Medical Coding (ICD-10, CPT)",
          "Health Information Systems",
          "Database Management",
          "Healthcare Law and Ethics",
          "Quality Management",
          "Electronic Health Records",
          "Health Data Analysis",
          "Classification Systems"
        ]
      },
      {
        year: 3,
        subjects: [
          "Advanced Medical Coding",
          "Healthcare Finance",
          "Privacy and Security",
          "Research Methods",
          "Project Management",
          "Leadership in Health Information",
          "Internship",
          "Professional Practice"
        ]
      }
    ],
    admissionProcess: [
      "Apply to colleges offering B.Sc Medical Record Science",
      "Merit-based selection or entrance examination",
      "Document verification",
      "Computer proficiency test (some colleges)",
      "Fee payment and admission",
      "Orientation program",
      "Begin academic training",
      "Industry internship coordination"
    ],
    topColleges: [
      {
        name: "All India Institute of Medical Sciences",
        location: "New Delhi",
        fees: "₹20,000 PA",
        ranking: "Premier Medical Institute"
      },
      {
        name: "Christian Medical College",
        location: "Vellore, Tamil Nadu",
        fees: "₹2.5 LPA",
        ranking: "Premier Medical College"
      },
      {
        name: "Kasturba Medical College",
        location: "Manipal, Karnataka",
        fees: "₹3 LPA",
        ranking: "Deemed University"
      },
      {
        name: "Amrita Institute of Medical Sciences",
        location: "Kochi, Kerala",
        fees: "₹2.8 LPA",
        ranking: "Private Medical College"
      },
      {
        name: "Government Medical College",
        location: "Chandigarh",
        fees: "₹35,000 PA",
        ranking: "Government Medical College"
      }
    ],
    skills: [
      "Medical Coding",
      "Data Analysis",
      "Computer Proficiency",
      "Attention to Detail",
      "Analytical Thinking",
      "Communication Skills",
      "Problem Solving",
      "Time Management",
      "Confidentiality",
      "Quality Control",
      "Technical Writing",
      "Continuous Learning"
    ],
    futureScope: "Health information management is rapidly evolving with electronic health records, artificial intelligence in coding, big data analytics, and telehealth documentation. Future opportunities include specialization in clinical data science, healthcare analytics, AI-assisted coding, blockchain in healthcare, and emerging roles in digital health platforms and personalized medicine data management.",
    faqs: [
      {
        question: "What is medical coding and why is it important?",
        answer: "Medical coding involves assigning standardized codes to diagnoses and procedures for billing, research, and statistical purposes. It's essential for healthcare reimbursement, quality analysis, and healthcare planning."
      },
      {
        question: "Do I need to be from a science background?",
        answer: "While science background is preferred, students from other streams can also pursue this course as it focuses more on information management, coding systems, and healthcare administration rather than clinical knowledge."
      },
      {
        question: "Can I work remotely in this field?",
        answer: "Yes, many positions in medical coding, health information management, and healthcare data analysis offer remote work opportunities, especially with companies that provide outsourced services."
      },
      {
        question: "What certifications are valuable in this field?",
        answer: "Popular certifications include CCS (Certified Coding Specialist), RHIA (Registered Health Information Administrator), and RHIT (Registered Health Information Technician) from AHIMA."
      },
      {
        question: "Is there scope for career advancement?",
        answer: "Yes, career paths include becoming department managers, HIM directors, healthcare consultants, compliance officers, or specializing in areas like revenue cycle management or clinical data management."
      }
    ]
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-black flex flex-col items-center justify-start py-8 px-2">
      <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 drop-shadow-glow mb-6 text-center animate-glow">
        {medicalRecordData.title} ({medicalRecordData.shortName})
      </h1>
      <div className="w-full max-w-4xl">
        <CourseTemplate courseData={medicalRecordData} />
      </div>
    </div>
  );
};

export default MedicalRecordScienceCoursePage;